export interface IAmount {
  currency?: string;
  value: number;
  oldValue?: number;
}

export interface IProduct {
  title: string;
  description?: string;
  orgId: number;
  createdAt: string;
  price: IAmount;
  externalId: string;
  salesTaxExcluded: boolean;
  quantity: number;
  sku: string;
  _id: string;
  cover?: string;
  restock?: boolean;
  tax?: number; // GST tax value
}

export type IProductForScanOrder = IProductOnline & {
  notes?: string;
  customerName?: string;
  uniqueId?: string;
};

export interface StoreDetailsPublicResponse {
  allowApplePay?: boolean;
  allowCard?: boolean;
  allowCash?: boolean;
  allowOnlineDelivery: boolean;
  allowOnlinePickup: boolean;
  bodyBackgroundColor: string;
  headerBackgroundColor: string;
  headerFontColor?: string;
  bodyFontColor?: string;
  city: string;
  country: string;
  externalStoreId: string;
  logo?: string;
  name: string;
  phone: string;
  requirePaymentForSto: boolean;
  requirePaymentForPickup: boolean;
  state: string;
  street1: string;
  street2?: string;
  theme: string;
  zipCode: number;
  tax?: number;
  invoiceFooterText?: string;
  invoiceFooterImage?: string;
}

export interface ProductCategory {
  image: string;
  name: string;
}

export interface IKitchenItem {
  createdAt: string;
  createdBy: string;
  id: string;
  orgId: number;
  productId: string;
  productName: string;
  status: "PROGRESS" | "READY" | "CREATED";
  tableId?: number;
  updatedAt?: string;
  updatedBy?: string;
  assignee?: string;
  restaurantTableProductId?: string;
  notes?: string;
}

export interface OrderStatusScanOrderResponse {
  productName: string;
  status: string;
}

export interface ScanToOrderTableResponse {
  name: string;
}
export interface ScanToOrderPayment {
  paymentData: IApplePayPaymentResponse;
  type: "APPLE" | "GOOGLE";
}

export interface IApplePayPaymentResponse {
  details: IApplePayPaymentResponseDetails;
  methodName: string; // https://apple.com/apple-pay
}

export interface IApplePayPaymentResponseDetails {
  token: IApplePayPaymentResponseToken;
}

export interface IApplePayPaymentResponseToken {
  paymentData: IApplePayPaymentResponsePaymentData;
  paymentMethod: IApplePayPaymentResponsePaymentMethod;
  transactionIdentifier: string;
}

export interface IApplePayPaymentResponsePaymentData {
  data: string;
  signature: string;
  version: string;
  header: IApplePayPaymentResponsePaymentDataHeader;
}

export interface IApplePayPaymentResponsePaymentDataHeader {
  ephemeralPublicKey: string;
  publicKeyHash: string;
  transactionId: string;
}

export interface IApplePayPaymentResponsePaymentMethod {
  displayName: string; // Discover 5486
  type: string; //credit
  network: string; // Discover
}

export interface ScanToOrderPayment {
  paymentData: IApplePayPaymentResponse;
  type: "APPLE" | "GOOGLE";
  amount?: number;
}

export interface IFee {
  id: string;
  isIncluded: boolean;
  name: string;
  orgId: number;
  type: "FLAT" | "PERCENT";
  username: string;
  value: number;
}

export type IFeeInSale = Pick<IFee, "name" | "type" | "value" | "id">;

export type IProductForSale = IProduct & {
  notes?: string;
  customerName?: string;
  totalQuantity?: number;
  totalPrice?: number;
  uniqueId?: string;
};

export interface IOnlineOrderCustomer {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  street1?: string;
  street2?: string;
  city?: string;
  state?: string;
  country?: string;
  zipcode?: string;
}

export interface IParseAddressResponse {
  city: string;
  street: string;
  zipcode: string;
  state: string;
  countryCode: string;
}

export interface IGetMobilePaymentByIdResponse {
  paymentType: string;
  amount: number;
  status: string;
  tax: number;
  total: number;
  customerPhone?: string;
}

export type IProductOnline = Pick<
  IProduct,
  | "_id"
  | "description"
  | "title"
  | "cover"
  | "price"
  | "salesTaxExcluded"
  | "tax"
>;

/** A bookable service shown on the public appointments page (a product). */
export type IBookableService = IProductOnline & {
  durationMinutes?: number;
};

/** A provider option in the appointment booking employee picker. */
export interface IPublicProvider {
  id: number;
  name: string;
}

/** An available booking slot returned by the availability endpoint. */
export interface IPublicSlot {
  time: string; // wall-clock HH:mm
  startTime: string; // ISO instant
  endTime: string; // ISO instant
  employeeId: number;
  employeeName: string;
}

export interface ICreatePublicAppointmentPayload {
  productIds: string[];
  employeeId?: number;
  startTime: string;
  customerName: string;
  customerPhone?: string;
  customerEmail?: string;
  notes?: string;
}

export interface IPublicAppointmentConfirmation {
  confirmationCode: string;
  startTime: string;
  status: string;
}

export enum ONLINE_ORDER_TYPE {
  BATCH_DELIVERY = "BATCH_DELIVERY",
  NONE = "NONE",
  SCAN_TO_ORDER = "SCAN_TO_ORDER",
}

/** Minimal store info needed to render the shared org header component. */
export interface IStoreHeaderDetails {
  logo?: string;
  name?: string;
  street1?: string;
  street2?: string;
  city?: string;
  state?: string;
  zipCode?: number;
  phone?: string;
}

/**
 * Hotel booking page store details — served by exhut-hms-service (its
 * online-settings look merged with the property info from business-service).
 */
export interface IHotelStoreDetails extends IStoreHeaderDetails {
  externalStoreId: string;
  headerBackgroundColor?: string;
  headerFontColor?: string;
  bodyBackgroundColor?: string;
  bodyFontColor?: string;
  country: string;
}

/** A bookable hotel room returned by the public availability search (hms). */
export interface IAvailableRoom {
  id: number;
  name: string;
  roomTypeName?: string;
  pricePerNight: number;
  capacity: number;
  description?: string;
  images: string[];
}

/** Search criteria for the public hotel room availability listing. */
export interface IAvailableRoomsSearch {
  checkInDate: string;
  checkOutDate: string;
  numberOfGuests?: number;
}

export enum PAYMENT_TYPE {
  CASH = "CASH",
  CARD = "CARD",
  PAYPAL = "PAYPAL",
  CHECK = "CHECK",
  OTHER = "OTHER",
  NONE = "NONE",
  ACH = "ACH",
  APPLE_PAY = "APPLE_PAY",
  GOOGLE_PAY = "GOOGLE_PAY",
  CARD_ONLINE = "CARD_ONLINE",
}

export enum AUTHORIZATION_TEMPLATE_CONSENT_ID {
  CUSTOMER_PAY_INVOICE_ONLINE_CARD = "3",
}

export type ICardDetails = {
  cardToken?: string;
  expiry?: string;
};

export enum ONLINE_ORDER_FULLFILMENT_TYPE {
  PICKUP = "PICKUP",
  DELIVERY = "DELIVERY",
}
